<script lang="ts">
  import { project, postBanners, orgOperators } from "$lib/services/store";
  import { getBannerImage } from "$lib/common/helpers/image";
  import type { Banner } from "@/src/types/banners/Banner";
  import type { Operator } from "@/src/types/components/operators/Operator";
  import Skeleton from "$lib/main/components/Skeleton.svelte";
  import { getAffiliateLinkCategories } from "$lib/common/enums/affiliateLinksCategories";
  import type { BlockStyling } from "@/src/types/components/Styling";
  import Terms from "$lib/admin/components/blocks/helpers/Terms.svelte";

  interface BannerProps {
    bannerIds: number[];
    styling: BlockStyling;
  }

  export let props: BannerProps | null = null;
  export let dataElement: string | undefined;

  if (!props) {
    props = {
      bannerIds: [],
      styling: {
        marginTop: "",
        marginBottom: "",
      },
    };
  }

  let allBanners: Banner[] = $postBanners || [];
  let operators: Operator[] = $orgOperators || [];
  let banners: Banner[] = [];
  let loadedImages: Record<string, boolean> = {};

  $: {
    if (props.bannerIds && props.bannerIds.length > 0 && allBanners.length > 0) {
      const bannerMap = new Map(allBanners.map((banner) => [banner.id, banner]));
      banners = props.bannerIds
        .map((id) => bannerMap.get(id))
        .filter((banner): banner is Banner => banner !== undefined && banner.status === true);
    } else if (allBanners.length > 0) {
      banners = allBanners.filter((banner) => banner.status === true);
    }
  }

  async function getOperator(banner: Banner): Promise<Operator | undefined> {
    if (!banner.operator_id) {
      return undefined;
    }

    return operators.find((op) => op.id === banner.operator_id);
  }

  async function getCtaLink(banner: Banner): Promise<string> {
    if (banner.cta_url_type !== "affiliate" && banner.cta_url) {
      return banner.cta_url;
    }

    if (banner.operator_id && banner.cta_affiliate_key) {
      const operator = operators.find((op) => op.id === banner.operator_id);

      if (operator && operator.name_lower) {
        const redirectSlug = $project.settings.redirectUrl || "spela";

        if (banner.cta_affiliate_key && banner.cta_affiliate_key !== "default") {
          const safeSpecialKeys = getAffiliateLinkCategories();
          if (safeSpecialKeys.includes(banner.cta_affiliate_key)) {
            return `/${redirectSlug}/${operator.name_lower}/${banner.cta_affiliate_key}`;
          }

          console.warn(`Unsupported special key: ${banner.cta_affiliate_key}, using base path instead`);
          return `/${redirectSlug}/${operator.name_lower}`;
        }

        return `/${redirectSlug}/${operator.name_lower}`;
      }

      if (banner.cta_url) {
        return banner.cta_url;
      }
    }

    return banner.cta_url || "#";
  }
  function handleImageLoad(imageUrl: string) {
    loadedImages[imageUrl] = true;
  }
</script>

{#if banners.length > 0}
  <div class="content-grid {props?.styling?.marginTop ?? ''} {props?.styling?.marginBottom ?? ''}">
    <div class="banners-grid {banners.length === 1 ? 'single-banner' : ''}" data-element={dataElement}>
      {#each banners as banner}
        {#if banner.cta_text || banner.operator_id}
          {#await getCtaLink(banner) then ctaUrl}
            <div class="banner-card" style="background-color: rgb({banner.bg_gradient_color})">
              <a href={ctaUrl} target="_blank" rel="nofollow noopener noreferrer" style="height: 100%;">
                <div class="banner-image-container">
                  {#if !loadedImages[getBannerImage(banner.image)]}
                    <Skeleton
                      height="100%"
                      width="100%"
                      baseColor="rgba({banner.bg_gradient_color}, 0.3)"
                      highlightColor="rgba({banner.bg_gradient_color}, 0.5)" />
                  {/if}
                  <div
                    class="banner-image {loadedImages[getBannerImage(banner.image)] ? 'loaded' : 'loading'}"
                    style="background-image: url({getBannerImage(banner.image)})"
                    on:load={() => handleImageLoad(getBannerImage(banner.image))}>
                    <img
                      src={getBannerImage(banner.image)}
                      alt={banner.title || "Banner"}
                      style="display: none;"
                      on:load={() => handleImageLoad(getBannerImage(banner.image))} />
                  </div>
                  <div
                    class="banner-gradient"
                    style="background: linear-gradient(to bottom, rgba(0, 0, 0, 0.0), rgba({banner.bg_gradient_color}) 70%)">
                  </div>
                </div>
                <div class="banner-content-container">
                  {#if banner.badge_text}
                    <div class="banner-badge">
                      {banner.badge_text}
                    </div>
                  {/if}
                  <div class="banner-content" style="background-color: rgba({banner.bg_gradient_color})">
                    <div class="banner-info">
                      <div class="banner-title">
                        {banner.title}
                      </div>
                      {#if banner.bullet_points && banner.bullet_points.length > 0}
                        <ul class="banner-bullet-points">
                          {#each banner.bullet_points as point}
                            <li>{point}</li>
                          {/each}
                        </ul>
                      {/if}
                    </div>
                    <div class="banner-cta-container">
                      <div class="banner-cta">
                        {banner.cta_text || "TILL CASINOT"}
                      </div>

                      {#await getOperator(banner) then operatorData}
                        <div class="banner-terms">
                          {#if banner.override_terms && banner.terms_text}
                            {@html banner.terms_text}
                          {:else if operatorData}
                            <Terms operator={operatorData} />
                          {/if}
                        </div>
                      {/await}
                    </div>
                  </div>
                </div>
              </a>
            </div>
          {/await}
        {/if}
      {/each}
    </div>
  </div>
{/if}

<style>
  .banners-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 20px;
  }

  @media (min-width: 768px) {
    .banners-grid {
      grid-template-columns: repeat(auto-fit, minmax(450px, 1fr));
      max-width: 100%;
    }
  }
  @media (min-width: 768px) {
    .banners-grid.single-banner .banner-card {
      max-width: 800px;
      width: 100%;
      margin-left: auto;
      margin-right: auto;
    }
    .banners-grid.single-banner .banner-card .banner-image-container {
      aspect-ratio: 171 / 59;
    }
  }

  .banner-card {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    min-height: auto;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  }

  .banner-card a {
    display: block;
    position: relative;
    width: 100%;
  }

  .banner-image-container {
    position: relative;
    width: 100%;
    aspect-ratio: 397 / 164;
    overflow: hidden;
    background-color: rgba(235, 236, 239, 0.3);
  }

  @media (min-width: 768px) {
    .banner-image-container {
      aspect-ratio: 141 / 59;
    }
  }

  .banner-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    z-index: 1;
    opacity: 0;
    transition: opacity 0.1s ease;
  }

  .banner-image.loaded {
    opacity: 1;
  }

  .banner-content-container {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    color: white;
  }

  .banner-gradient {
    position: absolute;
    width: 100%;
    height: 30%;
    bottom: 0;
    z-index: 2;
  }
  .banner-content {
    padding: 10px 40px 40px 40px;
    width: 100%;
    height: fit-content;
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .banner-badge {
    margin: auto auto 15px auto;
    padding: 6px 16px;
    background-color: #c03832;
    color: #fff;
    border-radius: 20px;
    font-size: 14px;
    z-index: 10;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    text-transform: uppercase;
  }

  .banner-title {
    font-size: 24px;
    line-height: 1.1;
    text-align: center;
    margin-bottom: 8px;
    font-weight: 600;
  }
  .banner-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  }

  .banner-bullet-points {
    padding-left: 0px;
    list-style-type: none;
    gap: 0px;
    margin-bottom: 20px;
    width: fit-content;
    align-self: flex-start;
  }

  .banner-bullet-points li {
    position: relative;
    padding-left: 20px;
    font-weight: normal;
    font-size: 1rem;
  }

  .banner-bullet-points li::before {
    content: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIiIGhlaWdodD0iMTciIHZpZXdCb3g9IjAgMCAxMiAxNyIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPGcgaWQ9IlZlY3RvciI+CjxwYXRoIGlkPSJWZWN0b3JfMiIgZD0iTTExLjQyODYgMTAuMjg0MUMxMS40Mjg2IDkuMjU0MTUgMTEuMTQ2NyA4LjI4ODA2IDEwLjY1NzMgNy40NDk4OEMxMC41NzEgNy42MTk0MSAxMC40ODE0IDcuNzc0MjUgMTAuMzg5OCA3LjkxNzEyQzkuNDMzOTEgOS40MDg1MSA4LjI1NDMyIDkuNTA5ODQgOC4yNTQzMiA5LjUwOTg0QzguNjY5MzQgOC40MTkwOSA4LjgwNDcwIDcuMzk0NTggOC43MDU3NCA2LjQzMDI4QzguNjUyNiA1LjkxMjU1IDguNTMyMTQgNS40MTIxNCA4LjM1MDc4IDQuOTI4MjZDNy4zNTcwMiAyLjI4NTg4IDQuOTE2OTkgMC4zNDY2NzYgNC43NjEyOCAwLjE5MDQzMEM1LjMzOTczIDMuNTkyODYgMy4xNjY3NiA1LjA5ODc0IDEuNzY1MDYgNi43NDY3MEMxLjA5NjEwIDcuNTM4OTYgMC41ODcyOCA4LjM0OTg0IDAuMjkxNjA5IDkuMjQ2NThDMC4xMDMzNjkgOS43NzA1NyAwIDEwLjMzMDQgMCAxMC45MTMwQzAgMTAuOTMxOCAwLjAwMTI5NDgxIDEwLjk1MDQgMC4wMDE1MDIxMyAxMC45NjkyQzAuMDAwNjg2MjI0IDExLjAxMDYgMCAxMS4wNTIgMCAxMS4wOTM5QzAgMTIuMzcwMiAwLjU2NDkzMyAxMy41NDExIDEuNDQ4NDQgMTQuNDI3MkMyLjQ5MjA2IDE1LjUwODkgNC4wMDkyMiAxNi4xOTA0IDUuNjk4ODcgMTYuMTkwNEM4Ljg0NjMgMTYuMTkwNCAxMS4zOTc4IDEzLjgyNzYgMTEuMzk3OCAxMC45MTMwQzExLjM5NzggMTAuODk4OSAxMS4zOTY4IDEwLjg1IDExLjM5NjcgMTAuODcwOUMxMS40MTcgMTAuNjc3OCAxMS40Mjg2IDEwLjQ4MjMgMTEuNDI4NiAxMC4yODQxWiIgZmlsbD0iI0U1NDAzOCIvPgo8L2c+Cjwvc3ZnPg==);
    color: #ff6b6b;
    position: absolute;
    left: 0;
    top: 3px;
  }

  .banner-cta-container {
    text-align: center;
  }

  .banner-cta {
    display: inline-block;
    padding: 12px 30px;
    background-color: #65c82d;
    color: white;
    text-decoration: none;
    border-radius: 6px;
    font-weight: 400;
    text-align: center;
    letter-spacing: 1px;
    transition:
      background-color 0.2s,
      transform 0.2s;
    margin-bottom: 10px;
    width: 100%;
  }

  .banner-cta:hover {
    background-color: #58ae28;
    transform: translateY(-2px);
  }

  .banner-terms {
    width: 100%;
    text-align: center;
    font-size: 0.7rem;
    font-weight: 300;
    line-height: 12px;
    color: rgba(255, 255, 255, 0.7);
    margin-top: 10px;
  }

  :global(.banner-cta-container .banner-terms a) {
    color: white;
    font-weight: inherit;
    text-decoration: underline;
    &:hover {
      text-decoration: none;
      color: white;
    }
  }
  @media (min-width: 768px) {
    .banner-card {
      min-height: 340px;
    }

    .banner-badge {
      position: absolute;
      top: 20px;
      left: 20px;
      margin-bottom: 0;
    }
    .banner-title {
      font-size: 1.1rem;
      text-align: left;
    }

    .banner-content-container {
      justify-content: space-between;
    }

    .banner-info {
      flex: 1;
    }

    .banner-cta-container {
      max-width: 220px;
    }

    .banner-content {
      padding: 20px;
      flex-direction: row;
      justify-content: space-between;
      align-items: flex-end;
      gap: 10px;
    }

    .banner-bullet-points {
      margin-bottom: 0px;
    }
    .banner-cta {
      max-width: 230px;
      margin-bottom: 0;
    }

    .banner-terms {
      font-size: 0.6rem;
    }
  }
  @media (max-width: 767px) {
    .banner-card {
      min-height: 500px;
    }
  }
</style>
